import { purchase } from '@prisma/client'
import { SpriteIconsKeys } from '@components/Sprites/SpriteIcon'
import { exhaustiveCheck } from '@helpers/helpers'
import { Dictionary } from '@hooks/useTranslation'

export type excludedPurchaseTypes = Exclude<purchase['type'], 'CRON_COLLECT' | 'BLINDBOX_OPEN' | 'CUSTOM_BANNERS'>

type FunctionType<T extends unknown> = (type: excludedPurchaseTypes, currency: purchase['currency'], isPositive: boolean, isMy: boolean) => T

export const getIconName: FunctionType<SpriteIconsKeys | undefined> = (type, currency, isPositive) => {
    const isRuby = currency === 'RUBY'
    switch (type) {
        case 'SKIP_DROP':
            return 'avatar_default'
        case 'PARTNER_PACK_PURCHASE':
            return isRuby ? 'ruby_out' : 'gold_out'
        case 'PURCHASED_TOY':
            return isPositive ? (isRuby ? 'ruby_in' : 'gold_in') : isRuby ? 'ruby_out' : 'gold_out'
        case 'COLLECTED_TOY':
            return isPositive ? (isRuby ? 'ruby_in' : 'gold_in') : isRuby ? 'ruby_out' : 'gold_out'
        case 'BURNED_TOY':
            return 'burned_toy'
        case 'PURCHASE':
            return isRuby ? 'ruby_in' : 'gold_in'
        case 'WITHDRAW':
            return isPositive ? 'declined' : 'ruby_out'
        case 'COMMISSION':
        case 'COMMISSION_ARTIST':
            return 'commission'
        case 'SUBSCRIPTION':
            return 'avatar_default'
        case 'SHARE':
        case 'CONFIRMATION_SHARING':
            return 'social_share'
        case 'BOUGHT_BOARD':
        case 'BOUGHT_BOARD_BG':
            return isRuby ? 'ruby_out' : 'gold_out'
        case 'REFERRAL':
            return undefined
        case 'BOUGHT_GAME':
        case 'GAME_WIN':
            return 'games_active'
        case 'SOCIAL_VERIFICATION':
        case 'USER_REGISTRATION':
            return 'avatar_default'
        case 'ADMIN':
            return isPositive ? (isRuby ? 'ruby_in' : 'gold_in') : isRuby ? 'ruby_out' : 'gold_out'
        case 'PROMO_CODE':
            return isPositive ? (isRuby ? 'ruby_in' : 'gold_in') : isRuby ? 'ruby_out' : 'gold_out'
        case 'MIGRATION':
            return 'avatar_default'
        case 'OFFER_FROZEN':
        case 'OFFER_RETURNED':
            return undefined
        case 'ROYALTY_FOR_BUYABLE_DROP':
            return 'royalty'
        case 'QUEST_EARN':
            return 'games_active'
        case 'BLINDBOX':
        case 'PURCHASED_BLINDBOX_FULLSET':
        case 'DIGITAL_DROP':
            return undefined
        case 'GIFT_TOY':
        case 'MINT_FEE':
        case 'RUBY_CASHBACK':
            return undefined
        default:
            exhaustiveCheck(type)
    }
}

export const getTitle: FunctionType<Dictionary | undefined> = (type, _, isPositive, isMy) => {
    switch (type) {
        case 'SKIP_DROP':
            return 'payment.history.page.title.drop.skipped'
        case 'PARTNER_PACK_PURCHASE':
            return 'payment.history.page.title.purchase'
        case 'PURCHASED_TOY':
        case 'DIGITAL_DROP':
            return isMy ? 'payment.history.page.title.sale' : 'payment.history.page.title.purchase'
        case 'COLLECTED_TOY':
            return 'payment.history.page.title.collected.drop'
        case 'BURNED_TOY':
            return 'payment.history.page.title.burned'
        case 'PURCHASE':
            return 'payment.history.page.title.purchase'
        case 'WITHDRAW':
            return isPositive ? 'payment.history.page.title.error' : 'payment.history.page.title.withdrawal'
        case 'COMMISSION':
        case 'COMMISSION_ARTIST':
            return 'payment.history.page.title.commission'
        case 'SUBSCRIPTION':
            return 'payment.history.page.title.subscription'
        case 'SHARE':
        case 'CONFIRMATION_SHARING':
            return 'payment.history.page.title.sharing'
        case 'BOUGHT_BOARD':
        case 'BOUGHT_BOARD_BG':
            return 'payment.history.page.title.purchase'
        case 'REFERRAL':
            return 'payment.history.page.title.referral'
        case 'BOUGHT_GAME':
        case 'GAME_WIN':
            return 'payment.history.page.title.games'
        case 'SOCIAL_VERIFICATION':
            return 'payment.history.page.title.social.networks'
        case 'USER_REGISTRATION':
            return 'payment.history.page.title.social.networks'
        case 'ADMIN':
        case 'PROMO_CODE':
            return isPositive ? 'payment.history.page.title.accrual' : 'payment.history.page.title.return'
        case 'MIGRATION':
            return 'payment.history.page.title.migration'
        case 'OFFER_FROZEN':
            return 'payment.history.page.title.purchase.offer'
        case 'OFFER_RETURNED':
            return 'payment.history.page.title.return'
        case 'ROYALTY_FOR_BUYABLE_DROP':
            return 'payment.history.page.title.royalty'
        case 'QUEST_EARN':
            return 'payment.history.page.title.quest'
        case 'BLINDBOX':
        case 'PURCHASED_BLINDBOX_FULLSET':
            return 'payment.history.page.title.purchase'
        case 'GIFT_TOY':
        case 'MINT_FEE':
        case 'RUBY_CASHBACK':
            return undefined
        default:
            exhaustiveCheck(type)
    }
}

export const getText: FunctionType<Dictionary | undefined> = (type, currency, isPositive, isMy) => {
    const isRuby = currency === 'RUBY'
    switch (type) {
        case 'SKIP_DROP':
            return 'payment.history.page.text.drop.skipped'
        case 'PARTNER_PACK_PURCHASE':
            return 'payment.history.page.text.purchase'
        case 'PURCHASED_TOY':
        case 'DIGITAL_DROP':
            return isMy ? 'payment.history.page.text.sale' : 'payment.history.page.text.purchase'
        case 'COLLECTED_TOY':
            return 'payment.history.page.text.collected.drop'
        case 'BURNED_TOY':
            return 'payment.history.page.text.burned'
        case 'PURCHASE':
            return isRuby ? 'payment.history.page.text.ruby.purchase' : 'payment.history.page.text.gold.purchase'
        case 'WITHDRAW':
            return isPositive ? 'payment.history.page.text.error' : 'payment.history.page.text.withdrawal.ruby'
        case 'COMMISSION':
        case 'COMMISSION_ARTIST':
            return 'payment.history.page.text.commission'
        case 'SUBSCRIPTION':
            return 'payment.history.page.text.subscription'
        case 'SHARE':
            return 'payment.history.page.text.sharing.post'
        case 'CONFIRMATION_SHARING':
            return 'payment.history.page.text.sharing.confirmation'
        case 'BOUGHT_BOARD':
        case 'BOUGHT_BOARD_BG':
            return 'payment.history.page.text.board.purchase'
        case 'REFERRAL':
            return 'payment.history.page.text.referral.award'
        case 'BOUGHT_GAME':
            return 'payment.history.page.text.games.purchased'
        case 'GAME_WIN':
            return 'payment.history.page.text.games.won'
        case 'SOCIAL_VERIFICATION':
            return 'payment.history.page.text.account.connection'
        case 'USER_REGISTRATION':
            return 'payment.history.page.text.registration'
        case 'ADMIN':
            return isPositive ? (isRuby ? 'payment.history.page.text.accrual.ruby' : 'payment.history.page.text.accrual.gold') : isRuby ? 'payment.history.page.text.return.ruby' : 'payment.history.page.text.return.gold'
        case 'PROMO_CODE':
            return isRuby ? 'payment.history.page.text.accrual.ruby.promocode' : 'payment.history.page.text.accrual.gold.promocode'
        case 'MIGRATION':
            return undefined
        case 'OFFER_FROZEN':
            return 'payment.history.page.text.purchase.offer'
        case 'OFFER_RETURNED':
            return 'payment.history.page.text.return'
        case 'ROYALTY_FOR_BUYABLE_DROP':
            return 'payment.history.page.text.royalty'
        case 'QUEST_EARN':
            return undefined
        case 'BLINDBOX':
        case 'PURCHASED_BLINDBOX_FULLSET':
            return 'payment.history.page.text.blindbox.purchase'

        case 'GIFT_TOY':
        case 'MINT_FEE':
        case 'RUBY_CASHBACK':
            return undefined
        default:
            exhaustiveCheck(type)
    }
}
